import React from 'react';
import { Box, Card, CardContent, Typography, Divider } from '@mui/material';
import { 
  Analytics as AnalyticsIcon,
  TrendingUp as TrendingUpIcon,
  TrendingFlat as TrendingFlatIcon,
  TrendingDown as TrendingDownIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';

const MarketBreadthChart = ({ marketBreadth }) => {
  const { t } = useTranslation();
  
  if (!marketBreadth || !marketBreadth.distribution) return null;

  // Prepare pie chart data
  const pieData = [
    { 
      name: t('rankings.strongBuy'), 
      value: marketBreadth.distribution.strong_buy, 
      color: '#11998e' 
    },
    { 
      name: t('rankings.buy'), 
      value: marketBreadth.distribution.buy, 
      color: '#38d9a9' 
    },
    { 
      name: t('rankings.hold'), 
      value: marketBreadth.distribution.hold, 
      color: '#ffa726' 
    },
    { 
      name: t('rankings.underweight'), 
      value: marketBreadth.distribution.underweight, 
      color: '#ff8787' 
    },
    { 
      name: t('rankings.avoid'), 
      value: marketBreadth.distribution.avoid, 
      color: '#ff6b6b' 
    },
  ].filter(item => item.value > 0); // Only show non-zero values

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      return (
        <Box
          sx={{
            bgcolor: 'background.paper',
            p: 1.5,
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: 2,
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
            {data.name}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {data.value} {t('dashboard.marketBreadth.sectors')} ({((data.value / marketBreadth.total_sectors) * 100).toFixed(1)}%)
          </Typography>
        </Box>
      );
    }
    return null;
  };

  const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text 
        x={x} 
        y={y} 
        fill="white" 
        textAnchor={x > cx ? 'start' : 'end'} 
        dominantBaseline="central"
        fontSize="12"
        fontWeight="600"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <Card 
      elevation={0}
      sx={{ 
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        height: '100%',
        bgcolor: 'background.paper',
        '&:hover': {
          bgcolor: 'background.paper',
          boxShadow: 'none',
        },
        transition: 'none',
      }}
    >
      <CardContent sx={{ p: 2.5, display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              mr: 1.5,
            }}
          >
            <AnalyticsIcon sx={{ fontSize: 20 }} />
          </Box>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: '1rem', lineHeight: 1.3 }}>
              {t('dashboard.marketBreadth.title')}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
              {t('dashboard.marketBreadth.subtitle')}
            </Typography>
          </Box>
        </Box>

        {/* Pie Chart */}
        <Box sx={{ width: '100%', flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <ResponsiveContainer width="100%" height="100%" minWidth={300} minHeight={200}>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={renderCustomLabel}
                outerRadius="75%"
                innerRadius="0%"
                fill="#8884d8"
                dataKey="value"
                paddingAngle={2}
                activeIndex={-1}
                activeShape={null}
              >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} cursor={false} wrapperStyle={{ zIndex: 1000 }} animationDuration={50} animationEasing="ease-out" />
              <Legend 
                verticalAlign="bottom" 
                height={32}
                iconSize={10}
                wrapperStyle={{ fontSize: '11px', paddingTop: '8px' }}
                formatter={(value, entry) => (
                  <span style={{ color: '#666', fontSize: '11px' }}>
                    {value}: <strong>{entry.payload.value}</strong>
                  </span>
                )}
              />
            </PieChart>
          </ResponsiveContainer>
        </Box>

        {/* Multi-Tier Classification */}
        <Box sx={{ 
          mt: 'auto',
          pt: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
        }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', mb: 1.5, display: 'block', fontWeight: 600 }}>
            {t('dashboard.marketBreadth.scoreDistribution')} (Score-Based)
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
            {/* Strong Sectors */}
            <Box sx={{ flex: 1, textAlign: 'center' }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                mb: 0.5,
              }}>
                <TrendingUpIcon sx={{ fontSize: 16, color: '#11998e', mr: 0.5 }} />
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#11998e', fontSize: '1.5rem', lineHeight: 1 }}>
                  {marketBreadth.strong_pct || 0}%
                </Typography>
              </Box>
              <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                {t('dashboard.marketBreadth.strong')}
              </Typography>
              <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.secondary', display: 'block' }}>
                (≥55)
              </Typography>
            </Box>

            <Divider orientation="vertical" flexItem sx={{ my: 0.5 }} />

            {/* Neutral Sectors */}
            <Box sx={{ flex: 1, textAlign: 'center' }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                mb: 0.5,
              }}>
                <TrendingFlatIcon sx={{ fontSize: 16, color: '#ffa726', mr: 0.5 }} />
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#ffa726', fontSize: '1.5rem', lineHeight: 1 }}>
                  {marketBreadth.neutral_pct || 0}%
                </Typography>
              </Box>
              <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                {t('dashboard.marketBreadth.neutral')}
              </Typography>
              <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.secondary', display: 'block' }}>
                (50-55)
              </Typography>
            </Box>

            <Divider orientation="vertical" flexItem sx={{ my: 0.5 }} />

            {/* Weak Sectors */}
            <Box sx={{ flex: 1, textAlign: 'center' }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                mb: 0.5,
              }}>
                <TrendingDownIcon sx={{ fontSize: 16, color: '#ff6b6b', mr: 0.5 }} />
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#ff6b6b', fontSize: '1.5rem', lineHeight: 1 }}>
                  {marketBreadth.weak_pct || 0}%
                </Typography>
              </Box>
              <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                {t('dashboard.marketBreadth.weak')}
              </Typography>
              <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.secondary', display: 'block' }}>
                (&lt;50)
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Bullish/Bearish Gauge */}
        <Box sx={{ pt: 2, pb: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', mb: 1, display: 'block', fontWeight: 600 }}>
            {t('dashboard.marketBreadth.investmentSentiment')} (Recommendation-Based)
          </Typography>
          
          {/* Gauge Bar */}
          <Box sx={{ position: 'relative', mb: 1 }}>
            <Box sx={{ 
              display: 'flex', 
              height: 24, 
              borderRadius: 1.5,
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'divider',
            }}>
              {/* Bullish Section */}
              <Box sx={{ 
                width: `${marketBreadth.bullish_pct || 0}%`,
                bgcolor: '#11998e',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'width 0.3s ease',
              }}>
                {(marketBreadth.bullish_pct || 0) > 15 && (
                  <Typography variant="caption" sx={{ color: 'white', fontWeight: 700, fontSize: '0.7rem' }}>
                    {marketBreadth.bullish_pct}%
                  </Typography>
                )}
              </Box>
              
              {/* Hold Section */}
              <Box sx={{ 
                width: `${marketBreadth.hold_pct || 0}%`,
                bgcolor: '#ffa726',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'width 0.3s ease',
              }}>
                {(marketBreadth.hold_pct || 0) > 15 && (
                  <Typography variant="caption" sx={{ color: 'white', fontWeight: 700, fontSize: '0.7rem' }}>
                    {marketBreadth.hold_pct}%
                  </Typography>
                )}
              </Box>
              
              {/* Bearish Section */}
              <Box sx={{ 
                width: `${marketBreadth.bearish_pct || 0}%`,
                bgcolor: '#ff6b6b',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'width 0.3s ease',
              }}>
                {(marketBreadth.bearish_pct || 0) > 15 && (
                  <Typography variant="caption" sx={{ color: 'white', fontWeight: 700, fontSize: '0.7rem' }}>
                    {marketBreadth.bearish_pct}%
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>

          {/* Gauge Labels */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#11998e' }} />
              <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                {t('dashboard.marketBreadth.bullishSectors')}: <strong>{marketBreadth.bullish_sectors || 0}</strong>
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#ffa726' }} />
              <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                {t('dashboard.marketBreadth.holdSectors')}: <strong>{marketBreadth.hold_sectors || 0}</strong>
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#ff6b6b' }} />
              <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                {t('dashboard.marketBreadth.bearishSectors')}: <strong>{marketBreadth.bearish_sectors || 0}</strong>
              </Typography>
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default MarketBreadthChart;

